import { useNavigate } from 'react-router-dom';
import { Plus, Users, UserCheck, UserX, UserPlus } from 'lucide-react';

import { useTranslation } from '../../i18n';
import useModal from '../../hooks/useModal';
import useListParams from '../../hooks/useListParams';
import {
  useUsers,
  useDashboardStats,
  useUnbanUser,
  useUnmuteUser,
} from '../../hooks/queries/useUserQueries';
import { IUserWithStats } from '../../types';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import UserListView from './UserListView';
import UserFormDialog from './UserFormDialog';
import UserBanDialog from './UserBanDialog';
import UserMuteDialog from './UserMuteDialog';
import UserDeleteDialog from './UserDeleteDialog';
import UserNotifyDialog from './UserNotifyDialog';

const PAGE_SIZE = 20;

interface IUserManagementProps {
  initialUserId?: string;
  onNavigateDetail?: (id: string) => void;
  onBackToList?: () => void;
  onNavigateRoom?: (roomId: string) => void;
}

const UserManagement = ({ onNavigateDetail }: IUserManagementProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { search, filter, page } = useListParams();

  const formDialog = useModal<IUserWithStats>();
  const banDialog = useModal<IUserWithStats>();
  const muteDialog = useModal<IUserWithStats>();
  const deleteDialog = useModal<IUserWithStats>();
  const notifyDialog = useModal<IUserWithStats>();

  const unbanUser = useUnbanUser();
  const unmuteUser = useUnmuteUser();

  const { data: dashboardStats } = useDashboardStats();

  const { data, isLoading } = useUsers({
    page,
    search,
    filter: filter || 'all',
    limit: PAGE_SIZE,
  });

  const users = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const handleViewDetail = (user: IUserWithStats) => {
    if (onNavigateDetail) {
      onNavigateDetail(user.id);
    } else {
      navigate(`/users/${user.id}`);
    }
  };

  const handleEdit = (user: IUserWithStats) => {
    formDialog.open(user);
  };

  const handleBan = (user: IUserWithStats) => {
    if (!user.isActive) {
      unbanUser.mutate(user.id);
    } else {
      banDialog.open(user);
    }
  };

  const handleMute = (user: IUserWithStats) => {
    if (user.isMuted) {
      unmuteUser.mutate(user.id);
    } else {
      muteDialog.open(user);
    }
  };

  const handleDelete = (user: IUserWithStats) => {
    deleteDialog.open(user);
  };

  const handleNotify = (user: IUserWithStats) => {
    notifyDialog.open(user);
  };

  const statCards = [
    {
      label: t('users.total_users'),
      value: (dashboardStats?.totalUsers ?? 0).toLocaleString(),
      Icon: Users,
      iconColor: 'text-blue-600',
    },
    {
      label: t('users.active_users'),
      value: (dashboardStats?.activeUsers ?? 0).toLocaleString(),
      Icon: UserCheck,
      iconColor: 'text-green-600',
    },
    {
      label: t('users.banned'),
      value: (dashboardStats?.bannedUsers ?? 0).toString(),
      Icon: UserX,
      iconColor: 'text-red-600',
    },
    {
      label: t('users.new_today'),
      value: `+${dashboardStats?.newTodayUsers ?? 0}`,
      Icon: UserPlus,
      iconColor: 'text-purple-600',
    },
  ];

  return (
    <div className="p-4 lg:p-8 space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('users.title')}
          </h1>
          <p className="text-base text-gray-600 mt-2">{t('users.subtitle')}</p>
        </div>
        <Button onClick={() => formDialog.open()} className="gap-2">
          <Plus className="w-4 h-4" />
          {t('users.add_user')}
        </Button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(({ label, value, Icon, iconColor }) => (
          <Card key={label} className="py-6 px-6">
            <CardContent className="p-0">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">
                    {label}
                  </div>
                  <div className="text-3xl font-bold text-foreground">
                    {value}
                  </div>
                </div>
                <Icon className={`w-8 h-8 ${iconColor}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <UserListView
        users={users}
        total={total}
        totalPages={totalPages}
        isLoading={isLoading}
        onViewDetail={handleViewDetail}
        onEdit={handleEdit}
        onBan={handleBan}
        onMute={handleMute}
        onDelete={handleDelete}
        onNotify={handleNotify}
      />

      <UserFormDialog
        isOpen={formDialog.isOpen}
        onClose={formDialog.close}
        user={formDialog.selectedItem}
      />

      <UserBanDialog
        isOpen={banDialog.isOpen}
        onClose={banDialog.close}
        user={banDialog.selectedItem}
      />

      <UserMuteDialog
        isOpen={muteDialog.isOpen}
        onClose={muteDialog.close}
        user={muteDialog.selectedItem}
      />

      <UserDeleteDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.close}
        user={deleteDialog.selectedItem}
      />

      <UserNotifyDialog
        isOpen={notifyDialog.isOpen}
        onClose={notifyDialog.close}
        user={notifyDialog.selectedItem}
      />
    </div>
  );
};

export default UserManagement;
